import { LS_KEY_ON_AUTH_REDIRECT_PATHNAME } from 'src/constants/localStorageKeys';
import { isAdminMode } from './storedToken';

// Routes that are never a useful destination. The entry/handoff ones ('/',
// '/home', '/login', '/signup') each bounce again on their own or need a token
// the expiring session no longer has. The onboarding ones are where
// `resolvePostAuthRoute` sends an un-onboarded user anyway, and the onboarding
// screen itself consumes this key on the way out — storing its own path there
// would just navigate it to itself. '/404' is nowhere.
const NON_RETURNABLE_PATHS = new Set([
  '/',
  '/home',
  '/login',
  '/signup',
  '/404',
  '/candidate-post',
  '/employer-post',
  '/placement-partner-post',
]);

/** A path on this origin, safe to redirect to. Browsers drop tab/CR/LF from
 * a URL before parsing it, so `/<LF>/evil.com` would navigate off-origin; a
 * backslash is normalised to a slash. Mirrors the landing app's `/login`
 * capture script. */
export const isSameOriginPath = (path: string): boolean => {
  const cleaned = path.replace(/[\t\n\r]/g, '');
  return (
    cleaned === path &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('\\')
  );
};

/** Remember where an expiring session was so `resolvePostAuthRoute` can put the
 * user back there after they sign in again. Without it a session that dies
 * mid-visit strands them on the marketing root with no way back to the page
 * they were reading.
 *
 * Same-origin paths only — the value is fed straight into a redirect later, so
 * an absolute or protocol-relative one would make it an open redirect. */
const rememberReturnPath = (
  path: string = window.location.pathname + window.location.search,
): void => {
  // A Debug Mode page is the impersonated learner's; never return to it.
  if (isAdminMode()) return;
  if (!isSameOriginPath(path)) return;
  if (NON_RETURNABLE_PATHS.has(path.split('?')[0])) return;
  try {
    localStorage.setItem(LS_KEY_ON_AUTH_REDIRECT_PATHNAME, path);
  } catch {
    // A storage quota/private-mode failure must not block the redirect that
    // follows this call.
  }
};

export default rememberReturnPath;
